/**
 * config.js — all player tunables in one place.
 *
 * Nothing data-dependent is hardcoded
 * here: frame count, fps, and camera all come from manifest.json at runtime.
 * The values below are only *fallbacks* used when the manifest omits a field.
 *
 * Every field can be overridden per-session by a URL query parameter, so a
 * link can pin a view, a frame, or a cache size without a rebuild.
 */

/** Parse `?a=1&b=2` into typed overrides. */
function readParams() {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

const params = readParams();

const num = (key, fallback) => {
  const raw = params.get(key);
  if (raw === null) return fallback;
  const v = Number(raw);
  return Number.isFinite(v) ? v : fallback;
};

const bool = (key, fallback) => {
  const raw = params.get(key);
  if (raw === null) return fallback;
  return raw === '' || raw === '1' || raw.toLowerCase() === 'true';
};

const str = (key, fallback) => params.get(key) ?? fallback;

/** Parse a `x,y,z` triple. Returns null when absent or malformed. */
const vec3 = (key) => {
  const raw = params.get(key);
  if (!raw) return null;
  const parts = raw.split(',').map(Number);
  return parts.length === 3 && parts.every(Number.isFinite) ? parts : null;
};

export const config = {
  // ---- data ------------------------------------------------------------
  /** Manifest location. Relative by default so viewer+data stay same-origin
   *  on GitHub Pages and no CORS config is needed. */
  manifestUrl: params.get('manifest'),

  /**
   * Library index listing every available sequence, used to populate the
   * sequence dropdown. A browser cannot list a directory over HTTP, so this
   * file is generated by tools/index_sequences.py (run automatically by the
   * converter). If it is missing, the player falls back to `manifestFallback`
   * and shows no dropdown.
   */
  indexUrl: str('index', 'data/index.json'),

  /** Used when there is no index.json and no ?manifest= override. */
  manifestFallback: 'data/manifest.json',

  /** Sequence id from the index to open first. Defaults to the first entry. */
  sequenceId: params.get('seq'),

  /** Overrides manifest.baseUrl when set. Empty manifest baseUrl means frame
   *  files resolve relative to the manifest's own URL. */
  baseUrlOverride: params.get('baseUrl'),

  // ---- streaming / memory -----------------------------------
  /** Resident-window size. `ahead` frames are preloaded in the playback
   *  direction, `behind` are retained for cheap backward scrubbing. Total
   *  GPU-resident meshes stay <= ahead + behind + 1.
   *
   *  MUST remain configurable — a generous window on capable hardware can
   *  hold a short sequence end-to-end, but it must never be hardcoded to a
   *  value tuned for one dataset. */
  preloadAhead: Math.max(0, num('ahead', 12)),
  preloadBehind: Math.max(0, num('behind', 2)),

  /** Max concurrent frame fetches.
   *
   *  Measured throughput on the reference dataset (7.2 MiB/frame, localhost):
   *    1 concurrent -> 3.7 frames/sec     4 concurrent -> 13.9 frames/sec
   *    2 concurrent -> 8.0 frames/sec     8 concurrent -> 17.5 frames/sec
   *  Decode, not fetch, is the bottleneck (raw fetch is only ~47 ms), so
   *  concurrency helps well past 1. Returns flatten after ~6. */
  maxConcurrentLoads: Math.max(1, num('concurrency', 6)),

  /** Failed-fetch retry policy. */
  retryAttempts: Math.max(0, num('retries', 2)),
  retryBaseDelayMs: 400,

  // ---- playback --------------------------------------------------------
  /** Only used when manifest.temporal.playbackFps is missing. */
  fallbackFps: num('fps', 24),

  /**
   * 'everyFrame' (default) shows every frame, running slower than the authored
   * fps when decode cannot keep up. 'realtime' follows the wall clock and drops
   * whatever is not decoded in time.
   *
   * Measured on the reference dataset: a 471k-splat frame costs ~150 ms to
   * fetch+decode and throughput plateaus near 17.5 frames/sec, so 'realtime'
   * at 24 fps drops most frames. Override with ?mode=realtime.
   */
  playbackMode: str('mode', 'everyFrame') === 'realtime' ? 'realtime' : 'everyFrame',

  /**
   * Frames to decode before playback starts (and after a stall). Prevents an
   * immediate stall on pressing play. 0 disables prebuffering.
   */
  prebufferFrames: Math.max(0, num('prebuffer', 6)),

  startPaused: bool('paused', false),
  loop: bool('loop', true),
  /** Initial frame (1-based, as shown in the UI). */
  startFrame: num('frame', 1),

  // ---- camera -------------------------------------------
  /** Session overrides for the initial view. Precedence is
   *  URL params > manifest.camera > these defaults. */
  cameraOverride: {
    position: vec3('camPos'),
    target: vec3('camTarget'),
    fov: params.has('fov') ? num('fov', 50) : null,
  },
  cameraDefaults: {
    position: [0, 1.2, 3.2],
    target: [0, 0, 0],
    up: [0, 1, 0],
    fov: 50,
  },
  near: 0.01,
  far: 1000,


  // ---- orientation -----------------------------------------------------
  /**
   * 180 deg rotation about X, mapping Y-down source data into THREE.js's Y-up
   * world. Spark's own README example does exactly this
   * (`quaternion.set(1, 0, 0, 0)`).
   *
   * This is NOT universal: it is a property of how each sequence was exported,
   * not of the player. The reference `excavator` needs it; `bogdanFly`, from
   * the same studio pipeline, renders upside down WITH it. So the authoritative
   * value lives per-sequence in `manifest.display.flipX`.
   *
   * Precedence: ?flipX= (session override) > manifest.display.flipX > this.
   * `null` here means "not specified", so the manifest wins.
   */
  flipX: params.has('flipX') ? bool('flipX', true) : null,
  /** Fallback when neither the URL nor the manifest specifies orientation. */
  flipXDefault: true,

  // ---- rendering -------------------------------------------------------
  /**
   * Spark 2.1.0 renders through THREE.WebGLRenderer (WebGL2) and ships no
   * WebGPU backend. This flag therefore only selects
   * antialiasing/precision hints, not an API.
   */
  antialias: bool('antialias', false),
  maxPixelRatio: num('maxPixelRatio', 2),
  background: str('bg', '#0b0d10'),

  // ---- audio (dormant until a track is added) ------------------------------------
  /** Only consulted when manifest.audio exists. With no audio block the
   *  AudioTrack module does nothing at all — no fetch, no UI, no logging. */
  audioDefaultMuted: bool('muted', true),
  audioDefaultVolume: Math.min(1, Math.max(0, num('volume', 1))),

  // ---- misc ------------------------------------------------------------
  /** Mirror frame+camera into the URL hash so a link restores the view. */
  deepLink: bool('deepLink', true),
  deepLinkThrottleMs: 400,
  showStats: bool('stats', false),
};

/** Read the initial camera/frame state encoded in the URL hash. */
export function readHashState() {
  if (typeof window === 'undefined' || !window.location.hash) return null;
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const parseVec = (key) => {
    const raw = hash.get(key);
    if (!raw) return null;
    const parts = raw.split(',').map(Number);
    return parts.length === 3 && parts.every(Number.isFinite) ? parts : null;
  };
  const frame = Number(hash.get('f'));
  return {
    frame: Number.isFinite(frame) && frame > 0 ? frame : null,
    position: parseVec('p'),
    target: parseVec('t'),
  };
}
